import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/search/SearchOverlay.css';
import MatchCard from '../shared/MatchCard';
import { Icon } from '../../utils/icons';
import { useLang } from '../../i18n';

const API = process.env.REACT_APP_API_BASE_URL;
const HISTORY_KEY = 'searchHistory';
const MAX_HISTORY = 8;

const SPORTS = [
  { id: 'football',          labelKey: 'search.sports.football',         icon: 'sport-football',          color: '#0f891f' },
  { id: 'basketball',        labelKey: 'search.sports.basketball',       icon: 'sport-basketball',        color: '#fca601' },
  { id: 'tennis',            labelKey: 'search.sports.tennis',           icon: 'sport-tennis',            color: '#f8c83c' },
  { id: 'ice-hockey',        labelKey: 'search.sports.iceHockey',        icon: null,                      color: '#3bc8f6', puck: true },
  { id: 'volleyball',        labelKey: 'search.sports.volleyball',       icon: 'sport-volleyball',        color: '#f67c3b' },
  { id: 'mma',               labelKey: 'search.sports.mma',              icon: 'sport-mma',               color: '#e84040' },
  { id: 'rugby',             labelKey: 'search.sports.rugby',            icon: 'sport-rugby',             color: '#a76b2f' },
  { id: 'handball',          labelKey: 'search.sports.handball',         icon: 'sport-handball',          color: '#e85cd1' },
  { id: 'table-tennis',      labelKey: 'search.sports.tableTennis',      icon: 'sport-tabletennis',       color: '#f6c344' },
  { id: 'american-football', labelKey: 'search.sports.americanFootball', icon: 'sport-american-football', color: '#ff6b35' },
];

function getHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch { return []; }
}
function saveHistory(term) {
  if (!term.trim()) return;
  const h = getHistory().filter((t) => t.toLowerCase() !== term.toLowerCase());
  localStorage.setItem(HISTORY_KEY, JSON.stringify([term, ...h].slice(0, MAX_HISTORY)));
}
function removeHistory(term) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(getHistory().filter((t) => t !== term)));
}

export default function SearchOverlay() {
  const { t } = useLang();
  const [open,     setOpen]     = useState(false);
  const [q,        setQ]        = useState('');
  const [focus,    setFocus]    = useState(false);
  const [history,  setHistory]  = useState([]);
  const [results,  setResults]  = useState([]);
  const [leagues,  setLeagues]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);

  const debounceRef = useRef(null);
  const inputRef    = useRef(null);
  const navigate    = useNavigate();
  const query       = q.trim();

  /* ── open / close ── */
  useEffect(() => {
    const handler = () => { setHistory(getHistory()); setOpen(true); };
    window.addEventListener('openSearch', handler);
    return () => window.removeEventListener('openSearch', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setQ('');
    setResults([]);
    setLeagues([]);
    setSearched(false);
    setLoading(false);
    clearTimeout(debounceRef.current);
  }, []);

  /* ── debounced search ── */
  const doSearch = useCallback(async (term) => {
    if (!term) { setResults([]); setLeagues([]); setSearched(false); return; }
    setLoading(true);
    setSearched(true);

    const keywords = term.toLowerCase().split(/\s+/).filter(k => k.length > 0);
    const hits = (text) => keywords.every(k => text.includes(k));

    const allMatches = [];
    const allLeagues = new Map();

    const push = (home, away, leagueName, sport, href, odds) => {
      const text = `${home} ${away} ${leagueName}`.toLowerCase();
      if (!hits(text)) return;
      allMatches.push({ home, away, live: false, odds, status: '', href });
      if (leagueName) {
        if (!allLeagues.has(leagueName))
          allLeagues.set(leagueName, { name: leagueName, sport, count: 0 });
        allLeagues.get(leagueName).count++;
      }
    };

    await Promise.allSettled([

      /* ── Matchs amateurs (internal) ── */
      (async () => {
        try {
          const r    = await fetch(`${API}/matchs/read_all.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
          });
          const data = await r.json();
          const list = data?.matches || data?.championships?.flatMap(c => c.matches || []) || [];
          list.forEach(m => {
            const home   = m.teamA?.name || '';
            const away   = m.teamB?.name || '';
            const league = m.league?.name || (typeof m.league === 'string' ? m.league : '') || m.championship || '';
            const mr     = m.odds?.matchResult;
            push(home, away, league, 'football', `/live/${m.id}`, [
              { v: mr && !isNaN(+mr.teamA) ? (+mr.teamA).toFixed(2) : '-' },
              { v: mr && !isNaN(+mr.draw)  ? (+mr.draw).toFixed(2)  : '-' },
              { v: mr && !isNaN(+mr.teamB) ? (+mr.teamB).toFixed(2) : '-' },
            ]);
          });
        } catch {}
      })(),

      /* ── Matchs pro (api-bet) ── */
      ...SPORTS.map(async ({ id }) => {
        try {
          const r    = await fetch(`${API}/api-bet/${id}/matchs.php`);
          const data = await r.json();
          if (!data.success || !data.fixtures?.length) return;
          data.fixtures.forEach(m => {
            const home   = m.teams?.home?.name || '';
            const away   = m.teams?.away?.name || '';
            const league = m.league?.name || '';
            const mw     = m.odds?.match_winner || [];
            push(home, away, league, id, `/match/${id}/${m.fixture?.id}`, [
              { v: mw.find(o => o.label === 'Home')?.odd != null ? String(mw.find(o => o.label === 'Home').odd) : '-' },
              { v: mw.find(o => o.label === 'Draw')?.odd != null ? String(mw.find(o => o.label === 'Draw').odd) : '-' },
              { v: mw.find(o => o.label === 'Away')?.odd != null ? String(mw.find(o => o.label === 'Away').odd) : '-' },
            ]);
          });
        } catch {}
      }),

    ]);

    setResults(allMatches.slice(0, 20));
    setLeagues([...allLeagues.values()].filter(l => hits(l.name.toLowerCase())).slice(0, 5));
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query) { setResults([]); setLeagues([]); setSearched(false); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => doSearch(query), 420);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  const commit = (term) => {
    const t = term.trim();
    if (!t) return;
    setQ(t);
    saveHistory(t);
    setHistory(getHistory());
  };

  const SPORT_MAP = Object.fromEntries(SPORTS.map((s) => [s.id, s]));

  if (!open) return null;

  return (
    <div className="sov">
      <div className="sov-backdrop" onClick={close} />

      <div className="sov-panel">

        {/* ── Barre de recherche ── */}
        <div className="sov-bar">
          <label className={'sov-field' + (focus ? ' is-focus' : '')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="sov-mag">
              <circle cx="11" cy="11" r="7" stroke="#757490" strokeWidth="1.8" />
              <path d="M16.5 16.5L21 21" stroke="#757490" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              placeholder={t('search.placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocus(true)}
              onBlur={() => setFocus(false)}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(q); }}
            />
            {q && (
              <button className="sov-clear" onClick={() => { setQ(''); setResults([]); setLeagues([]); setSearched(false); inputRef.current?.focus(); }}>
                <svg width="9" height="9" viewBox="0 0 9 9">
                  <path d="M1 1l7 7M8 1L1 8" stroke="#9b9ab0" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </label>
          <button className="sov-cancel" onClick={close}>{t('search.cancel')}</button>
        </div>

        {/* ── Corps scrollable ── */}
        <div className="sov-body">

          {/* État vide : historique + sports */}
          {query === '' && (
            <>
              {history.length > 0 && (
                <>
                  <div className="sov-sec">
                    <h3>{t('search.recentSearches')}</h3>
                    <button className="sov-clear-all" onClick={() => { localStorage.removeItem(HISTORY_KEY); setHistory([]); }}>{t('search.clear')}</button>
                  </div>
                  <div className="sov-chips">
                    {history.map((r) => (
                      <button className="sov-chip" key={r} onClick={() => commit(r)}>
                        {r}
                        <span className="sov-chip-x" onClick={(e) => { e.stopPropagation(); removeHistory(r); setHistory(getHistory()); }}>×</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="sov-sec"><h3>{t('search.exploreBySport')}</h3></div>
              <div className="sov-sports">
                {SPORTS.map((s) => (
                  <button key={s.id} className="sov-sport" onClick={() => { close(); navigate(`/${s.id}`); }}>
                    {s.puck
                      ? <svg viewBox="0 0 12 8.571" width="16" height="11" fill={s.color} style={{ flex: 'none' }}><path d="M12 4.286v1.071C12 7.132 9.314 8.571 6 8.571 2.745 8.571.096 7.183.003 5.452L0 5.357V4.286C0 6.061 2.686 7.5 6 7.5c3.314 0 6-1.439 6-2.214zM6 0c3.314 0 6 1.439 6 3.214C12 4.99 9.314 6.429 6 6.429 2.686 6.429 0 4.989 0 3.214 0 1.439 2.686 0 6 0z" /></svg>
                      : <Icon name={s.icon} w={16} h={16} color={s.color} />
                    }
                    <span>{t(s.labelKey)}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Spinner */}
          {query !== '' && loading && (
            <div className="sov-loading">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ animation: 'sov-spin .8s linear infinite' }}>
                <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.1)" strokeWidth="2" />
                <path d="M12 3a9 9 0 018.66 6.5" stroke="#76e1ff" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          )}

          {/* Résultats */}
          {query !== '' && !loading && searched && (
            leagues.length === 0 && results.length === 0 ? (
              <div className="sov-empty">{t('search.noResults', { query: q })}</div>
            ) : (
              <>
                {leagues.length > 0 && (
                  <>
                    <div className="sov-sec"><h3>{t('search.competitions')}</h3></div>
                    {leagues.map((l) => (
                      <button className="sov-league-row" key={l.name} onClick={() => { close(); navigate(`/${l.sport}`); }}>
                        <Icon name={SPORT_MAP[l.sport]?.icon || 'sport-football'} w={16} h={16} color={SPORT_MAP[l.sport]?.color || '#76e1ff'} />
                        <span className="sov-league-name">{l.name}</span>
                        <span className="sov-league-count">{l.count} match{l.count > 1 ? 's' : ''}</span>
                      </button>
                    ))}
                  </>
                )}
                {results.length > 0 && (
                  <>
                    <div className="sov-sec"><h3>{t('search.matches')} ({results.length})</h3></div>
                    <div className="sov-matches" onClick={close}>
                      {results.map((m, i) => <MatchCard key={i} match={m} />)}
                    </div>
                  </>
                )}
              </>
            )
          )}

        </div>
      </div>
    </div>
  );
}
